import type { TeacherClass } from 'src/class/class-provider';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { agentTrpc } from 'src/lib/agent-trpc';
import { useTodayStates } from 'src/layouts/header/use-today-periods';

import { DELIVERY_QUERY_KEY } from 'src/sections/lesson-plans/lesson-page/use-lesson-delivery';

import { wrapUp, type WrapUp } from './wrap-up-model';

// ----------------------------------------------------------------------

/**
 * The caller's own lessons still waiting for a "Mark taught" (epic #647): what the
 * Dashboard's Wrap up card lists and the nav badge counts. One agent read
 * (`lessonDelivery.unmarked`), shared by both through the query cache; today's lessons join
 * once the teacher's last period has ended (`useTodayStates`, re-read every minute).
 */
export function useWrapUp(classes: TeacherClass[]): WrapUp & { loading: boolean; today: string | null } {
  const ids = useMemo(() => classes.map((c) => c.id).sort(), [classes]);
  const q = useQuery({
    queryKey: [DELIVERY_QUERY_KEY, 'unmarked', ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
    queryFn: () => agentTrpc.lessonDelivery.unmarked({ classIds: ids }),
  });
  const states = useTodayStates(classes);
  const w = useMemo(() => wrapUp(q.data?.lessons ?? [], states), [q.data, states]);
  return { ...w, loading: q.isPending && ids.length > 0, today: q.data?.today ?? null };
}
